import React, { useRef, useState, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSearchParams } from 'react-router-dom';

import axios from 'axios';
import { set } from 'date-fns';

import LoginUsuarioExterno from '../../components/UsuarioExterno/LoginUsuario';
import { useLoginStore } from '../../stores/AuthStatusStore';

interface FormData {
  nome: string;
  cpf: string;
  cnpj: string;
}

export default function VerificaCredenciado(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const captchaRef = useRef<ReCAPTCHA>(null);
  const [isFisica, setIsFisica] = useState(true);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companyData, setCompanyData] = useState<any>({});
  const [recaptchaValido, setRecaptchaValido] = useState(false);
  const [error, setError] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [form, setForm] = useState<FormData>({
    nome: '',
    cpf: '',
    cnpj: '',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const tokenURL = searchParams.get('token');

  const isLogado = useLoginStore(x => x.login);

  useEffect(() => {
    setShowCaptcha(false);

    if (isLogado) {
      const timer = setTimeout(() => {
        setShowCaptcha(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isLogado]);

  const inputStyle =
    'border border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-300 rounded-lg p-3 w-full shadow-sm transition-all duration-300';

  const formatCPF = (value: string): string => {
    value = value.replace(/\D/g, '');
    value = value.replace(/(\d{3})(\d)/, '$1.$2');
    value = value.replace(/(\d{3})(\d)/, '$1.$2');
    value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    return value;
  };

  const formatCNPJ = (value: string): string => {
    value = value.replace(/\D/g, '');
    value = value.replace(/^(\d{2})(\d)/, '$1.$2');
    value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
    value = value.replace(/\.(\d{3})(\d)/, '.$1/$2');
    value = value.replace(/(\d{4})(\d)/, '$1-$2');
    return value;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'cpf') {
      formattedValue = formatCPF(value);
    } else if (name === 'cnpj') {
      formattedValue = formatCNPJ(value);
    }

    setForm(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleInitialSubmit = async () => {
    if (!recaptchaValido) {
      alert('Por favor, complete o reCAPTCHA.');
      return;
    }

    const urlBase = process.env.REACT_APP_OG_API_URL;

    try {
      setIsLoading(true);

      if (isFisica) {
        if (!form.nome || !form.cpf) {
          alert('Por favor, preencha todos os campos obrigatórios.');
          setIsLoading(false);
          return;
        }
        const url = `${urlBase}/info/VerificaCredenciado?tipoPessoa=1&nome=${encodeURIComponent(
          form.nome,
        )}&cpf=${form.cpf.replace(/[.\-/]/g, '')}`;
        const retorno = await axios.get(url);

        if (retorno.status === 200) {
          setCompanyData(retorno.data);
        } else {
          setError(retorno.data);
          alert(retorno.data.message || 'Erro ao buscar Credenciado.');
          setIsLoading(false);
        }
      } else {
        if (!form.cnpj) {
          alert('O CNPJ é obrigatório para Pessoa Jurídica.');
          setIsLoading(false);
          return;
        }

        const url = `${urlBase}/info/VerificaCredenciado?tipoPessoa=2&cnpj=${form.cnpj.replace(
          /[.\-/]/g,
          '',
        )}`;
        const retorno = await axios.get(url);

        if (retorno.status === 200) {
          setCompanyData(retorno.data);
        } else {
          setError(retorno.data);
          alert(retorno.data.message || 'Erro ao consultar CNPJ.');
          setIsLoading(false);
        }
      }
      setShowCompanyForm(true);
    } catch (err: any) {
      alert('Erro na consulta.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onCaptchaVerify = (token: string | null) => {
    setRecaptchaValido(!!token);
  };

  const novaConsulta = () => {
    setShowCompanyForm(false);
    setForm({
      nome: '',
      cpf: '',
      cnpj: '',
    });
    setCompanyData({});
    setRecaptchaValido(false);
    setIsFisica(true);
    setError('');
    if (captchaRef.current) captchaRef.current.reset();
  };

  return !isLogado ? (
    <div className="mx-auto mt-20 p-10 py-20 border rounded shadow">
      <h2 className="text-[2rem] font-semibold text-primary-main mt-12 text-center">
        Login validação prospect para credenciado
      </h2>
      <LoginUsuarioExterno tokenRecuperacao={tokenURL} />
    </div>
  ) : (
    <div className="mx-auto mt-20 p-10 py-20 border rounded shadow">
      {!showCompanyForm ? (
        <>
          <div className="max-w-md mx-auto p-4">
            <div className="flex items-center mb-4">
              <span className={`mr-2 ${isFisica ? 'font-bold' : ''}`}>
                Pessoa Física
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!isFisica}
                  onChange={() => setIsFisica(!isFisica)}
                />
                <div
                  className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 
                  peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] 
                  after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border 
                  after:rounded-full after:h-5 after:w-5 after:transition-all dark:bg-gray-700 peer-checked:bg-blue-600"
                ></div>
              </label>
              <span className={`ml-2 ${!isFisica ? 'font-bold' : ''}`}>
                Pessoa Jurídica
              </span>
            </div>

            {isFisica ? (
              <div className="p-5 gap-4 mt-5">
                <label className="block text-sm font-medium text-gray-700 mb-1 p-1">
                  Nome
                </label>
                <input
                  type="text"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  className={inputStyle}
                  placeholder="Digite o nome a ser consultado"
                />

                <label className="block text-sm font-medium text-gray-700 mb-1 p-1">
                  CPF
                </label>
                <input
                  type="text"
                  name="cpf"
                  value={form.cpf}
                  onChange={handleChange}
                  className={inputStyle}
                  placeholder="Digite o CPF"
                />
              </div>
            ) : (
              <div className="p-5 gap-4 mt-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CNPJ
                </label>
                <input
                  type="text"
                  name="cnpj"
                  value={form.cnpj}
                  onChange={handleChange}
                  className={inputStyle}
                  placeholder="Digite o CNPJ"
                />
              </div>
            )}

            <div className="mt-4" style={{ minHeight: '78px' }}>
              {showCaptcha && (
                <ReCAPTCHA
                  key="captcha-consulta-unique"
                  ref={captchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                  onChange={onCaptchaVerify}
                />
              )}
            </div>

            {!showCompanyForm && !isLoading && (
              <button
                onClick={handleInitialSubmit}
                className="mt-5 bg-blue-500 text-white px-4 py-2 rounded"
              >
                Avançar
              </button>
            )}

            {isLoading && (
              <div className="flex items-center justify-center mt-5">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-blue-500">Carregando...</span>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-center items-center">
            <div className="bg-white shadow-lg rounded-lg p-8 flex flex-col gap-8">
              <div className="flex flex-col">
                <span className="uppercase text-gray-500 text-xs font-normal">
                  Nome Fantasia:
                </span>
                <strong className="text-neutral-600 normal-case text-sm">
                  {companyData.nomeFilial}
                </strong>
              </div>

              <div className="flex flex-col">
                <span className="uppercase text-gray-500 text-xs font-normal">
                  Razão Social:
                </span>
                <strong className="text-neutral-600 normal-case text-sm">
                  {companyData.RazaoSocial}
                </strong>
              </div>

              <div className="flex flex-col">
                <span className="uppercase text-gray-500 text-xs font-normal">
                  Estado:
                </span>
                <strong className="text-neutral-600 normal-case text-sm">
                  {companyData.UF}
                </strong>
              </div>

              <div className="flex flex-col">
                <span className="uppercase text-gray-500 text-xs font-normal">
                  Município:
                </span>
                <strong className="text-neutral-600 normal-case text-sm">
                  {companyData.Municipio}
                </strong>
              </div>
            </div>
          </div>

          <button
            onClick={novaConsulta}
            className="mt-5 bg-blue-500 text-white px-4 py-2 rounded"
          >
            Voltar
          </button>
        </>
      )}
    </div>
  );
}
